import * as Models from "@gigalot/data-models";

class AnimalQueue {
  private items: Models.Animal[] = [];
  private head = 0;
  private itemCallback: ((animal: Models.Animal) => Promise<void>) | undefined = undefined;
  private queueFinishedCallback: (() => Promise<void>) | undefined = undefined;
  private idleResolvers: (() => void)[] = [];
  private isBusy = false;

  get length() {
    return this.items.length - this.head;
  }

  hasCallback() {
    return this.itemCallback !== undefined;
  }

  //Callback that is called per item as queue is processed in processQueue()
  setItemCallback(cb: (animal: Models.Animal) => Promise<void>) {
    this.itemCallback = cb;
  }

  setQueueFinishedCallback(cb: () => Promise<void>) {
    this.queueFinishedCallback = cb;
  }

  enqueue(i: Models.Animal | Models.Animal[]) {
    if (Array.isArray(i)) this.items.push(...i);
    else this.items.push(i);
    this.processQueue();
  }

  waitForIdle() {
    if (!this.isBusy && this.length === 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      this.idleResolvers.push(resolve);
    });
  }

  find(predicate: (animal: Models.Animal) => boolean) {
    for (let i = this.head; i < this.items.length; i++) {
      const animal = this.items[i];
      if (predicate(animal)) return animal;
    }
    return undefined;
  }

  filter(predicate: (animal: Models.Animal) => boolean) {
    const ret: Models.Animal[] = [];
    for (let i = this.head; i < this.items.length; i++) {
      const animal = this.items[i];
      if (predicate(animal)) ret.push(animal);
    }
    return ret;
  }

  removeFirst(predicate: (animal: Models.Animal) => boolean) {
    for (let i = this.head; i < this.items.length; i++) {
      if (!predicate(this.items[i])) continue;
      this.items.splice(i, 1);
      this.compactIfNeeded();
      return true;
    }
    return false;
  }

  private compactIfNeeded() {
    if (this.head === 0) return;
    if (this.head >= this.items.length) {
      this.items = [];
      this.head = 0;
      return;
    }
    if (this.head >= 1000 && this.head * 2 >= this.items.length) {
      this.items = this.items.slice(this.head);
      this.head = 0;
    }
  }

  dequeue() {
    if (this.head >= this.items.length) return undefined;
    const ret = this.items[this.head];
    this.head++;
    this.compactIfNeeded();
    return ret;
  }

  async processQueue() {
    if (!this.itemCallback) throw Error("No callback given to animal queue.")
    if (this.isBusy) return;
    this.isBusy = true;
    let a: Models.Animal | undefined;
    while (a = this.dequeue()) {
      await this.itemCallback?.(a)
    }
    await this.queueFinishedCallback?.();
    this.isBusy = false;
    this.idleResolvers.splice(0).forEach((resolve) => resolve());
  }

  clear() {
    this.items = [];
    this.head = 0;
  }

}

let animalQueue = new AnimalQueue();

export { animalQueue };
